import { useActionState } from "react";
import { createTraitAction } from "@/actions/traits";
import { Button } from "@/components/Button";
import { CodeCell, FormCell, FormCheckboxCell } from "@/components/FormCell";
import styles from "@/components/FormRow.module.css";
import { LinkButton } from "@/components/LinkButton";
import { useCodeFromLabel } from "@/components/useCodeFromLabel";
import { initialFormState } from "@/lib/formState";
import { useFormRedirect } from "@/lib/useFormRedirect";

const FORM_ID = "trait-draft";

type TraitDraftRowProps = {
  // The listing owns the rows, so a save has to tell it to read again.
  onSaved: () => void;
};

export const TraitDraftRow = ({ onSaved }: TraitDraftRowProps) => {
  const [state, formAction, isPending] = useActionState(
    createTraitAction,
    initialFormState,
  );

  useFormRedirect(state, onSaved);

  // The code derives from the label until someone edits it, so the row owns
  // both fields rather than each cell owning its own.
  const { label, code, setLabel, setCode } = useCodeFromLabel(
    state.values.label,
    state.values.code,
  );

  return (
    <tr>
      <CodeCell
        form={FORM_ID}
        name="code"
        label="Code"
        isRowHeader
        placeholder="PLAYS_GUITAR"
        value={code}
        onValueChange={setCode}
        errors={state.fieldErrors.code}
      />
      <FormCell
        form={FORM_ID}
        name="label"
        label="Label"
        placeholder="Plays guitar"
        value={label}
        onValueChange={setLabel}
        errors={state.fieldErrors.label}
      />
      <FormCell
        form={FORM_ID}
        name="selfDeclareQuestion"
        label="Self-declare question"
        placeholder="Can you play the guitar?"
        defaultValue={state.values.selfDeclareQuestion}
        errors={state.fieldErrors.selfDeclareQuestion}
      />
      <FormCheckboxCell
        form={FORM_ID}
        name="isOnboardingQuestion"
        label="Ask at signup"
      />
      <FormCell
        form={FORM_ID}
        name="sortOrder"
        label="Sort order"
        placeholder="0"
        inputMode="numeric"
        defaultValue={state.values.sortOrder}
        errors={state.fieldErrors.sortOrder}
      />
      {/* Required by, Granted by, Status and the actions column, none of which
          a draft has. */}
      <td className={styles.actions} colSpan={4}>
        <form id={FORM_ID} action={formAction} hidden />
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          disabled={isPending}
        >
          {isPending ? "Creating..." : "Create"}
        </Button>
        <LinkButton href="/traits" variant="secondary">
          Cancel
        </LinkButton>
      </td>
    </tr>
  );
};
